package com.mgacreative.hotel.feature.admin.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.domain.model.FloorFeature
import com.mgacreative.hotel.domain.model.Room
import com.mgacreative.hotel.domain.model.RoomPosition
import com.mgacreative.hotel.domain.repository.RoomRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class FloorPlanState(
    val rooms: List<Room> = emptyList(),
    val positions: Map<String, RoomPosition> = emptyMap(), // roomId -> position
    val features: List<FloorFeature> = emptyList(), // Asansör, merdiven vb.
    val isLoading: Boolean = false,
    val isSaving: Boolean = false
)

class FloorPlanViewModel(
    private val roomRepository: RoomRepository,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _state = MutableStateFlow(FloorPlanState())
    val state: StateFlow<FloorPlanState> = _state.asStateFlow()

    fun loadData() {
        viewModelScope.launch {
            _state.value = _state.value.copy(isLoading = true)
            val rooms = roomRepository.getRooms()
            val positions = roomRepository.getRoomPositions()
            val features = roomRepository.getFloorFeatures()
            val posMap = positions.associateBy { it.roomId }
            _state.value = _state.value.copy(
                rooms = rooms,
                positions = posMap,
                features = features,
                isLoading = false
            )
        }
    }

    private fun generateUUID(): String {
        val chars = "0123456789abcdef"
        val raw = (1..32).map { chars.random() }.joinToString("")
        return "${raw.substring(0, 8)}-${raw.substring(8, 12)}-4${raw.substring(13, 16)}-a${raw.substring(17, 20)}-${raw.substring(20, 32)}"
    }

    fun getFeaturesByFloor(floor: Int): List<FloorFeature> {
        return _state.value.features.filter { it.floorNumber == floor }
    }

    fun addFloorFeature(floorNumber: Int, featureType: String) {
        val tenantId = tenantSession.currentTenantId ?: "00000000-0000-0000-0000-000000000000"
        val existingCount = _state.value.features.count { it.floorNumber == floorNumber }
        val newFeature = FloorFeature(
            id = generateUUID(),
            tenantId = tenantId,
            floorNumber = floorNumber,
            featureType = featureType,
            label = if (featureType == "ELEVATOR") "Asansör" else "Merdiven",
            posX = 20f + (existingCount * 85f) % 360f,
            posY = 20f
        )
        _state.value = _state.value.copy(
            features = _state.value.features + newFeature
        )
        viewModelScope.launch {
            roomRepository.saveFloorFeature(newFeature)
        }
    }

    fun updateFeaturePosition(featureId: String, posX: Float, posY: Float) {
        val current = _state.value.features.find { it.id == featureId } ?: return
        val updated = current.copy(posX = posX, posY = posY)
        _state.value = _state.value.copy(
            features = _state.value.features.map { if (it.id == featureId) updated else it }
        )
        viewModelScope.launch {
            roomRepository.saveFloorFeature(updated)
        }
    }

    fun deleteFloorFeature(featureId: String) {
        _state.value = _state.value.copy(
            features = _state.value.features.filterNot { it.id == featureId }
        )
        viewModelScope.launch {
            roomRepository.deleteFloorFeature(featureId)
        }
    }

    /** Oda konumunu güncelle — önce local state, sonra Supabase */
    fun updateRoomPosition(roomId: String, floorNumber: Int, posX: Float, posY: Float) {
        val tenantId = tenantSession.currentTenantId ?: return
        val updated = RoomPosition(
            id = _state.value.positions[roomId]?.id,
            tenantId = tenantId,
            roomId = roomId,
            floorNumber = floorNumber,
            posX = posX,
            posY = posY
        )
        // Optimistic update
        _state.value = _state.value.copy(
            positions = _state.value.positions + (roomId to updated)
        )
        viewModelScope.launch {
            roomRepository.saveRoomPosition(updated)
        }
    }

    /** Oda ekleme formunda kat seçimi ile yeni oda ekle */
    fun addRoom(roomNumber: String, roomType: String, floorNumber: Int) {
        viewModelScope.launch {
            val tenantId = tenantSession.currentTenantId ?: "00000000-0000-0000-0000-000000000000"
            val newRoom = Room(
                tenantId = tenantId,
                roomType = roomType,
                roomNumber = roomNumber,
                status = "CLEAN",
                floorNumber = floorNumber
            )
            val added = roomRepository.addRoom(newRoom)
            if (added != null) {
                _state.value = _state.value.copy(
                    rooms = _state.value.rooms + added
                )
            }
        }
    }

    /** Katlar — floor_number alanına göre ya da oda numarasından türet */
    fun getFloors(): List<Int> {
        return _state.value.rooms
            .mapNotNull { room ->
                room.floorNumber ?: deriveFloorFromNumber(room.roomNumber)
            }
            .distinct()
            .sorted()
    }

    fun getRoomsByFloor(floor: Int): List<Room> {
        return _state.value.rooms.filter { room ->
            (room.floorNumber ?: deriveFloorFromNumber(room.roomNumber)) == floor
        }
    }

    private fun deriveFloorFromNumber(roomNumber: String): Int {
        val digits = roomNumber.filter { it.isDigit() }
        return when {
            digits.length >= 3 -> digits.dropLast(2).toIntOrNull() ?: 1
            digits.isNotEmpty() -> digits.first().digitToInt()
            else -> 1
        }
    }
}
